import { useState, FormEvent } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Contact() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const { ref: leftRef, visible: leftVisible } = useScrollReveal();
  const { ref: rightRef, visible: rightVisible } = useScrollReveal();
  const { ref: mapRef, visible: mapVisible } = useScrollReveal();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);

    const message = formData.get('message') as string;

    if (message && message.length > 500) {
      setStatus('error');
      return;
    }

    setStatus('sending');

    const params = new URLSearchParams();

    formData.forEach((value, key) => {
      params.append(key, value as string);
    });

    try {
      const res = await fetch('/send-mail.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      const result = await res.json();

      if (res.ok && result.success) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      id="contato"
      className="relative py-24 overflow-hidden"
      style={{
        background:
          'linear-gradient(140deg, #0c0c0c 0%, #131313 30%, #111111 60%, #0e0e0e 100%)',
      }}
    >
      {/* Premium decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/20 to-transparent"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/10 to-transparent"></div>
        <div
          className="absolute -top-10 -right-10 w-96 h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(192,192,192,0.05) 0%, transparent 65%)',
          }}
        ></div>
        <div
          className="absolute bottom-1/4 left-0 w-72 h-72 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(160,140,100,0.04) 0%, transparent 70%)',
          }}
        ></div>
        <div className="absolute top-0 right-0 w-px h-full bg-gradient-to-b from-transparent via-[#C0C0C0]/8 to-transparent"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left - Info */}
          <div
            ref={leftRef as React.RefObject<HTMLDivElement>}
            className={`transition-all duration-700 ${
              leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
            }`}
          >
            <span className="inline-block text-[#C0C0C0] text-sm font-semibold tracking-widest uppercase mb-4">
              Entre em Contato
            </span>

            <h2
              className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Fale com a <span className="text-[#D4D4D4]">Miki Motors</span>
            </h2>

            <p className="text-white/60 text-base leading-relaxed mb-10">
              Entre em contato conosco para solicitação de orçamentos, dúvidas, contato comercial
              ou reclamações. Nossa equipe está pronta para te atender!
            </p>

            {/* Contact cards */}
            <div className="flex flex-col gap-4">
              {[
                {
                  icon: 'ri-map-pin-line',
                  label: 'Endereço',
                  value: 'Av. Mal. Rondon, 2206 – Jardim Chapadão – Campinas – SP',
                  href: 'https://maps.google.com/?q=Av.+Mal.+Rondon,+2206+Campinas+SP',
                },
                {
                  icon: 'ri-phone-line',
                  label: 'Telefone',
                  value: '(19) 4141-9185 / (19) 4141-9355',
                  href: 'tel:+551941419185',
                },
                {
                  icon: 'ri-whatsapp-line',
                  label: 'WhatsApp',
                  value: '(19) 9 97889-998',
                  href: 'https://api.whatsapp.com/send/?phone=5519997889998&text&type=phone_number&app_absent=0',
                },
                {
                  icon: 'ri-mail-line',
                  label: 'E-mail',
                  value: 'mikimotors@mikimotors.com.br',
                  href: 'mailto:mikimotors@mikimotors.com.br',
                },
              ].map((item, i) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel="noopener noreferrer"
                  className={`flex items-start gap-4 bg-[#0D0D0D] border border-white/5 rounded-2xl p-5 hover:border-[#C0C0C0]/30 transition-all duration-500 cursor-pointer group ${
                    leftVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                  }`}
                  style={{ transitionDelay: leftVisible ? `${200 + i * 80}ms` : '0ms' }}
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#C0C0C0]/10 flex-shrink-0 group-hover:bg-[#C0C0C0]/20 transition-colors">
                    <i className={`${item.icon} text-[#C0C0C0] text-lg`}></i>
                  </div>

                  <div>
                    <div className="text-white/40 text-xs mb-1">{item.label}</div>
                    <div className="text-white text-sm font-medium">{item.value}</div>
                  </div>
                </a>
              ))}
            </div>

            {/* Social */}
            <div className="flex gap-3 mt-8">
              {[
                {
                  icon: 'ri-facebook-fill',
                  href: 'https://www.facebook.com/mikimotorsimports/',
                  label: 'Facebook',
                },
                {
                  icon: 'ri-instagram-line',
                  href: 'https://www.instagram.com/mikimotorsimports/',
                  label: 'Instagram',
                },
                {
                  icon: 'ri-youtube-fill',
                  href: 'https://www.youtube.com/channel/UCuv-P15BIG537o22DSL9Y2g',
                  label: 'YouTube',
                },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-11 h-11 flex items-center justify-center rounded-full bg-[#141414] border border-white/10 text-white/60 hover:border-[#C0C0C0] hover:text-[#C0C0C0] transition-all duration-300 cursor-pointer"
                >
                  <i className={`${s.icon} text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Right - Form */}
          <div
            ref={rightRef as React.RefObject<HTMLDivElement>}
            className={`bg-[#0D0D0D] border border-white/5 rounded-3xl p-8 md:p-10 transition-all duration-700 delay-200 ${
              rightVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
            }`}
          >
            <h3
              className="text-white text-xl font-bold mb-6"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Envie uma Mensagem
            </h3>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C0C0C0]/20 mb-4">
                  <i className="ri-check-line text-[#C0C0C0] text-3xl"></i>
                </div>

                <h4 className="text-white text-xl font-bold mb-2">Mensagem Enviada!</h4>
                <p className="text-white/50 text-sm">Entraremos em contato em breve.</p>

                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-6 text-[#C0C0C0] text-sm hover:underline cursor-pointer"
                >
                  Enviar outra mensagem
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white/40 text-xs mb-2 block">Nome</label>
                    <input
                      type="text"
                      name="nome"
                      placeholder="Seu nome"
                      required
                      className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C0C0C0]/50 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="text-white/40 text-xs mb-2 block">Telefone</label>
                    <input
                      type="tel"
                      name="telefone"
                      placeholder="(19) 9 0000-0000"
                      required
                      className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C0C0C0]/50 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-white/40 text-xs mb-2 block">E-mail</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="seu@email.com"
                    required
                    className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C0C0C0]/50 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-white/40 text-xs mb-2 block">Veículo</label>
                  <input
                    type="text"
                    name="veiculo"
                    placeholder="Ex: BMW 320i 2020"
                    className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C0C0C0]/50 transition-colors"
                  />
                </div>

                <div>
                  <label className="text-white/40 text-xs mb-2 block">
                    Mensagem <span className="text-white/20">({charCount}/500)</span>
                  </label>

                  <textarea
                    name="message"
                    placeholder="Descreva o serviço que precisa ou sua dúvida..."
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-[#0D0D0D] border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#C0C0C0]/50 transition-colors resize-none"
                  />

                  {charCount >= 500 && (
                    <p className="text-red-400 text-xs mt-1">Limite de 500 caracteres atingido.</p>
                  )}
                </div>

                {status === 'error' && (
                  <p className="text-red-400 text-sm">Erro ao enviar. Tente novamente.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending' || charCount > 500}
                  className="flex items-center justify-center gap-3 bg-[#C0C0C0] text-black font-bold px-8 py-4 rounded-xl hover:bg-white transition-all duration-300 disabled:opacity-60 cursor-pointer whitespace-nowrap"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Enviar Mensagem
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Map */}
        <div
          ref={mapRef as React.RefObject<HTMLDivElement>}
          className={`mt-16 rounded-3xl overflow-hidden h-72 border border-white/5 transition-all duration-700 ${
            mapVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <iframe
            title="Miki Motors Localização"
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3675.5!2d-47.0!3d-22.9!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sAv.+Mal.+Rondon%2C+2206+-+Jardim+Chapad%C3%A3o%2C+Campinas+-+SP!5e0!3m2!1spt-BR!2sbr!4v1"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
